/**
 * The two sides of the site, and the only place a link from one to the other is made
 * (`docs/library-first-plan.md` §2).
 *
 * **Play** is the library and everything around it — what a player lands on, at
 * `boardflight.app`. **Create** is the dashboard, at `create.boardflight.app`. They are one
 * bundle on two hosts: the same files, and which side a page is on is read from the address it
 * was loaded at. Every link that crosses from one side to the other goes through `createHref` or
 * `playHref`, and nothing else builds an origin.
 *
 * **Two hosts is a fact about the build, not about the code.** The origins come from
 * `VITE_PLAY_ORIGIN` and `VITE_CREATE_ORIGIN`. With both set, a page served from one of them
 * knows its side, links across are absolute, and a path that belongs to the other side is sent
 * there. With either missing — a development server, a preview, a self-hosted copy on one name —
 * or on any origin that is neither, one origin is both sides and every link is a path on it, which
 * is how the site ran before there were two.
 */

const clean = (origin: string | undefined): string => (origin ?? '').replace(/\/+$/, '');

const PLAY_ORIGIN = clean(import.meta.env.VITE_PLAY_ORIGIN as string | undefined);
const CREATE_ORIGIN = clean(import.meta.env.VITE_CREATE_ORIGIN as string | undefined);

export type Surface = 'play' | 'create';

/** Where the dashboard starts: it finds the account a team, or the page that makes one. */
export const CREATE_HOME = '/teams';

/**
 * Which side an origin is, or `null` where it is both.
 *
 * Takes the origins rather than reading them, so the rule can be stated — and tested — without a
 * build: `surface()` below is this, asked about the page.
 */
export function surfaceOf(origin: string, play: string, create: string): Surface | null {
  if (play === '' || create === '' || play === create) return null;
  if (origin === create) return 'create';
  if (origin === play) return 'play';
  return null;
}

/** Which side this page is on, or `null` where one origin is both. */
export function surface(): Surface | null {
  if (typeof window === 'undefined') return null;
  return surfaceOf(window.location.origin, PLAY_ORIGIN, CREATE_ORIGIN);
}

/** A page of the creator dashboard, from anywhere. */
export function createHref(path: string = CREATE_HOME): string {
  return surface() === 'play' ? `${CREATE_ORIGIN}${path}` : path;
}

/** A page of the library or the public site, from anywhere. */
export function playHref(path: string): string {
  return surface() === 'create' ? `${PLAY_ORIGIN}${path}` : path;
}

/**
 * The library's origin, for an address that is written down rather than followed: a share link
 * or a redeem link a creator copies out of the dashboard and pastes somewhere else. Such a link
 * has to name the host a stranger should open, whichever host it was copied on — the page's own
 * origin is the right answer only where one origin is both sides.
 */
export function playOrigin(): string {
  return surface() === 'create' ? PLAY_ORIGIN : window.location.origin;
}

/**
 * The paths only the dashboard draws: a team and everything in it, a game by its older address,
 * the playground's sign-in coming back from a provider, the account, an invitation, and the two
 * names the dashboard's own front door has had.
 */
const CREATE_PATHS = ['/teams', '/games', '/apps', '/playground', '/account', '/welcome', '/invite'];

/**
 * The paths both sides draw, each for itself: signing in and what follows from it. A provider's
 * sign-in returns to the origin that started it, and a link from an email opens wherever it was
 * sent — neither is the other side's to take over.
 */
const SHARED_PATHS = ['/signin', '/signup', '/forgot', '/reset', '/verify', '/auth'];

const under = (path: string, roots: readonly string[]): boolean =>
  roots.some((root) => path === root || path.startsWith(`${root}/`));

/**
 * Which side a path belongs to, or `null` for one both sides draw.
 *
 * `/` is both: each host has a front door of its own. Everything not named above is the public
 * site's — the library, a share link, the guides, the legal pages, and whatever is added next —
 * so a new public page needs no line here, and a new dashboard page under `/teams` needs none
 * either.
 */
export function surfaceOfPath(path: string): Surface | null {
  if (path === '/' || under(path, SHARED_PATHS)) return null;
  return under(path, CREATE_PATHS) ? 'create' : 'play';
}

/**
 * Where a page that is on the wrong side should be instead: the same path, query and fragment on
 * the other host — or `null` when it is where it belongs.
 *
 * This is what keeps every address that was ever written down working. An invitation in an inbox,
 * a bookmark, a link a plugin printed last year all name `boardflight.app/teams/…`; a share link
 * pasted into the dashboard's address bar names a game. Neither host turns the other's pages away —
 * it passes them on.
 */
export function acrossFrom(here: Surface | null, location: { pathname: string; search: string; hash: string }): string | null {
  if (here === null) return null;
  const owner = surfaceOfPath(location.pathname);
  if (owner === null || owner === here) return null;
  return `${owner === 'create' ? CREATE_ORIGIN : PLAY_ORIGIN}${location.pathname}${location.search}${location.hash}`;
}
